import {
  Badge,
  Box,
  Button,
  ButtonBase,
  Card,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Popover,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Notifications as BellIcon,
  NotificationsNone as BellOutlineIcon,
  DoneAll as MarkAllIcon,
  OpenInNew as ViewAllIcon,
  Campaign as SystemIcon,
  AccountBalance as LoanIcon,
  TrendingUp as InvestIcon,
  Payments as PaymentIcon,
  VerifiedUser as KycIcon,
  PhoneAndroid as DeviceIcon,
  Security as SecurityIcon,
  Warning as AlertIcon,
  Circle as DotIcon,
} from "@mui/icons-material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { NotificationsService } from "../../../pages/Notifications/Notifications.service";
import { INotification, TNotificationType } from "../../../pages/Notifications/Notifications.interface";

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

const POLL_INTERVAL_MS = 60_000; // poll every 60 s

const TYPE_META: Record<
  string,
  { icon: React.ReactNode; color: string }
> = {
  SYSTEM:     { icon: <SystemIcon sx={{ fontSize: 16 }} />,   color: "#6366f1" },
  LOAN:       { icon: <LoanIcon sx={{ fontSize: 16 }} />,     color: "#f59e0b" },
  INVESTMENT: { icon: <InvestIcon sx={{ fontSize: 16 }} />,   color: "#10b981" },
  PAYMENT:    { icon: <PaymentIcon sx={{ fontSize: 16 }} />,  color: "#3b82f6" },
  KYC:        { icon: <KycIcon sx={{ fontSize: 16 }} />,      color: "#8b5cf6" },
  DEVICE:     { icon: <DeviceIcon sx={{ fontSize: 16 }} />,   color: "#06b6d4" },
  SECURITY:   { icon: <SecurityIcon sx={{ fontSize: 16 }} />, color: "#ef4444" },
  ALERT:      { icon: <AlertIcon sx={{ fontSize: 16 }} />,    color: "#f97316" },
};

const getTypeMeta = (type: TNotificationType) =>
  TYPE_META[type?.toUpperCase()] ?? { icon: <BellOutlineIcon sx={{ fontSize: 16 }} />, color: "#6b7280" };

const timeAgo = (dateStr: string): string => {
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60)  return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};

/* ------------------------------------------------------------------ */
/* Notification Row                                                   */
/* ------------------------------------------------------------------ */

const NotifRow = ({
  notif,
  onMarkRead,
}: {
  notif: INotification;
  onMarkRead: (id: string) => void;
}) => {
  const theme = useTheme();
  const meta = getTypeMeta(notif.notification_type);

  return (
    <ButtonBase
      onClick={() => !notif.is_read && onMarkRead(notif.id)}
      sx={{
        display: "flex",
        width: "100%",
        textAlign: "left",
        px: 2,
        py: 1.5,
        gap: 1.5,
        alignItems: "flex-start",
        bgcolor: notif.is_read
          ? "transparent"
          : alpha(theme.palette.primary.main, 0.04),
        borderLeft: "3px solid",
        borderLeftColor: notif.is_read ? "transparent" : meta.color,
        transition: "background-color 0.15s",
        "&:hover": {
          bgcolor: alpha(theme.palette.action.hover, 0.7),
        },
      }}
    >
      {/* Icon bubble */}
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "8px",
          bgcolor: alpha(meta.color, 0.12),
          color: meta.color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          mt: "1px",
        }}
      >
        {meta.icon}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Typography
            variant="body2"
            fontWeight={notif.is_read ? 500 : 700}
            sx={{
              fontSize: 13,
              lineHeight: 1.3,
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 1,
              WebkitBoxOrient: "vertical",
            }}
          >
            {notif.title}
          </Typography>
          {!notif.is_read && (
            <DotIcon sx={{ fontSize: 8, color: meta.color, flexShrink: 0, mt: "4px" }} />
          )}
        </Stack>
        <Typography
          variant="caption"
          color="text.primary"
          sx={{
            display: "block",
            mt: 0.25,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {notif.message}
        </Typography>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5 }}>
          <Chip
            label={notif.notification_type}
            size="small"
            sx={{
              height: 16,
              fontSize: 9,
              fontWeight: 700,
              bgcolor: alpha(meta.color, 0.1),
              color: meta.color,
              letterSpacing: 0.3,
            }}
          />
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>
            {timeAgo(notif.created_at)}
          </Typography>
        </Stack>
      </Box>
    </ButtonBase>
  );
};

/* ================================================================== */
/* Main Popover Component                                            */
/* ================================================================== */

const NotificationsPopover = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const anchorRef = useRef<HTMLButtonElement>(null);

  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<INotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);

  /* ── Fetch unread count (background poll) ── */
  const fetchCount = useCallback(async () => {
    try {
      const { unread_count } = await NotificationsService.getUnreadCount();
      setUnreadCount(unread_count);
    } catch {
      // Silently fail — don't toast on background poll
    }
  }, []);

  useEffect(() => {
    fetchCount();
    const timer = setInterval(fetchCount, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [fetchCount]);

  /* ── Fetch latest notifications on popover open ── */
  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const data = await NotificationsService.getNotifications({
        page: 1,
        page_size: 12,
        ordering: "-created_at",
      });
      setNotifications(Array.isArray(data) ? data : data?.results || []);
    } catch {
      toast.error("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  /* ── Mark single read ── */
  const handleMarkRead = async (id: string) => {
    try {
      await NotificationsService.markRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true, read_at: new Date().toISOString() } : n))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch {
      toast.error("Failed to mark as read");
    }
  };

  /* ── Mark all read ── */
  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    try {
      await NotificationsService.markAllRead();
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, is_read: true, read_at: n.read_at ?? new Date().toISOString() }))
      );
      setUnreadCount(0);
      toast.success("All notifications marked as read");
    } catch {
      toast.error("Failed to mark all as read");
    } finally {
      setMarkingAll(false);
    }
  };

  const handleViewAll = () => {
    setOpen(false);
    navigate("/admin/notifications");
  };

  const unreadInList = notifications.filter((n) => !n.is_read).length;

  return (
    <>
      {/* Bell button */}
      <Tooltip title="Notifications">
        <IconButton
          ref={anchorRef}
          onClick={() => setOpen(true)}
          size="small"
          sx={{
            position: "relative",
            color: open ? "primary.main" : "text.primary",
            border: "1px solid",
            borderColor: open ? "primary.main" : "divider",
            borderRadius: 1.5,
            p: 0.75,
            transition: "all 0.15s",
            "&:hover": {
              bgcolor: alpha(theme.palette.primary.main, 0.06),
              borderColor: "primary.main",
              color: "primary.main",
            },
          }}
        >
          <Badge
            badgeContent={unreadCount}
            max={99}
            color="error"
            sx={{
              "& .MuiBadge-badge": {
                fontSize: 9,
                minWidth: 16,
                height: 16,
                fontWeight: 700,
              },
            }}
          >
            {unreadCount > 0 ? (
              <BellIcon sx={{ fontSize: 19 }} />
            ) : (
              <BellOutlineIcon sx={{ fontSize: 19 }} />
            )}
          </Badge>
        </IconButton>
      </Tooltip>

      {/* Popover */}
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          elevation: 8,
          sx: {
            width: 380,
            maxHeight: 520,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            mt: 1,
          },
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2.5,
            py: 1.75,
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle1" fontWeight={700}>
              Notifications
            </Typography>
            {unreadCount > 0 && (
              <Chip
                label={unreadCount}
                size="small"
                color="error"
                sx={{ height: 18, fontSize: 10, fontWeight: 700, minWidth: 24 }}
              />
            )}
          </Stack>

          {unreadInList > 0 && (
            <Tooltip title="Mark all as read">
              <IconButton
                size="small"
                onClick={handleMarkAllRead}
                disabled={markingAll}
                sx={{ color: "primary.main" }}
              >
                {markingAll ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <MarkAllIcon sx={{ fontSize: 17 }} />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Stack>

        {/* Notification list */}
        <Box sx={{ flex: 1, overflowY: "auto", "&::-webkit-scrollbar": { width: 4 } }}>
          {loading ? (
            <Stack spacing={0} divider={<Divider />}>
              {[...Array(5)].map((_, i) => (
                <Box key={i} sx={{ px: 2, py: 1.75, display: "flex", gap: 1.5 }}>
                  <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: 1, flexShrink: 0 }} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton width="70%" height={16} sx={{ mb: 0.5 }} />
                    <Skeleton width="90%" height={13} sx={{ mb: 0.5 }} />
                    <Skeleton width="35%" height={12} />
                  </Box>
                </Box>
              ))}
            </Stack>
          ) : notifications.length === 0 ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <BellOutlineIcon sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
              <Typography variant="body2" color="text.primary">
                You're all caught up!
              </Typography>
              <Typography variant="caption" color="text.disabled">
                No notifications yet
              </Typography>
            </Box>
          ) : (
            <Stack divider={<Divider />}>
              {notifications.map((n) => (
                <NotifRow key={n.id} notif={n} onMarkRead={handleMarkRead} />
              ))}
            </Stack>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Button
            fullWidth
            onClick={handleViewAll}
            endIcon={<ViewAllIcon sx={{ fontSize: 14 }} />}
            sx={{
              py: 1.25,
              textTransform: "none",
              fontWeight: 600,
              fontSize: 13,
              color: "primary.main",
              borderRadius: 0,
              "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.06) },
            }}
          >
            View all notifications
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default NotificationsPopover;